
import React, { useState } from 'react';

// To connect to your Google Sheet:
// 1. Create a Google Sheet.
// 2. Go to Extensions > Apps Script.
// 3. Paste the code provided in the conversation below.
// 4. Deploy as Web App (Access: Anyone).
const GOOGLE_SHEET_WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbw0MedBrPtXW_8rg9QtXK79EbdE0vhQkd6ogYr42FnYkSVl7dnIXT3QJ5_zPvDQ5A/exec'; 

const StrategyLab: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    industry: '',
    size: '',
    phone: ''
  });
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const submitToSheets = async (data: typeof formData) => {
    if (!GOOGLE_SHEET_WEBHOOK_URL) {
      console.warn("Google Sheet Webhook URL not set. Lead not synced.");
      return;
    }

    try {
      // Using URLSearchParams is the most reliable way to send data to GAS 
      // without triggering CORS pre-flight blocks.
      const params = new URLSearchParams();
      params.append('name', data.name);
      params.append('email', data.email);
      params.append('industry', data.industry);
      params.append('size', data.size);
      params.append('phone', data.phone);
      params.append('timestamp', new Date().toLocaleString());

      // We use no-cors because Google Apps Script redirects after POST, 
      // which standard fetch will block.
      await fetch(GOOGLE_SHEET_WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors', 
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });
      console.log("Data sent to Google Sheets");
    } catch (err) {
      console.error("Failed to sync lead to Google Sheets:", err);
    }
  };

  const handleGenerate = async () => {
    if (!formData.industry || !formData.size || !captchaChecked) return;
    
    setLoading(true);
    try {
      // 1. Capture Lead to Sheets
      await submitToSheets(formData);
      
      // We simulate a brief processing period for high-end feel
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lab" className="py-24 md:py-40 bg-[#0a0a0a]">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-6xl font-syne font-bold mb-6 uppercase">STRATEGY LAB</h2>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto">Enter your information below and unlock strategic insights designed for scale and dominance.</p>
        </div>

        <div className="max-w-3xl mx-auto bg-[#0f0f0f] border border-white/5 p-8 md:p-16 relative">
          {!submitted ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              <div className="col-span-1">
                <label className="block text-[10px] uppercase tracking-widest font-bold mb-3 opacity-50">Full Name</label>
                <input 
                  type="text" 
                  name="name"
                  placeholder="John Doe" 
                  className="w-full bg-transparent border-b border-white/20 pb-4 focus:border-white outline-none transition-colors"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="col-span-1">
                <label className="block text-[10px] uppercase tracking-widest font-bold mb-3 opacity-50">Email Address</label>
                <input 
                  type="email" 
                  name="email"
                  placeholder="john@example.com" 
                  className="w-full bg-transparent border-b border-white/20 pb-4 focus:border-white outline-none transition-colors"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-widest font-bold mb-3 opacity-50">Industry Vertical</label>
                <input 
                  type="text" 
                  name="industry"
                  placeholder="e.g. Entertainment Tech" 
                  className="w-full bg-transparent border-b border-white/20 pb-4 focus:border-white outline-none transition-colors"
                  value={formData.industry}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-widest font-bold mb-3 opacity-50">Current Size</label>
                <input 
                  type="text" 
                  name="size"
                  placeholder="e.g. Pre-Series A" 
                  className="w-full bg-transparent border-b border-white/20 pb-4 focus:border-white outline-none transition-colors"
                  value={formData.size}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="col-span-2 mb-12">
                <label className="block text-[10px] uppercase tracking-widest font-bold mb-3 opacity-50">Phone Number</label>
                <input 
                  type="tel" 
                  name="phone"
                  placeholder="+1 (555) 000-0000" 
                  className="w-full bg-transparent border-b border-white/20 pb-4 focus:border-white outline-none transition-colors"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                />
              </div>

              <div className="col-span-2">
                <div className="mb-10 p-5 bg-white/5 border border-white/10 flex items-center justify-between rounded-sm">
                   <div className="flex items-center space-x-4">
                      <input 
                        type="checkbox" 
                        id="captcha" 
                        className="w-6 h-6 rounded border-white/20 bg-transparent cursor-pointer accent-white"
                        checked={captchaChecked}
                        onChange={(e) => setCaptchaChecked(e.target.checked)}
                        disabled={loading}
                      />
                      <label htmlFor="captcha" className="text-sm font-medium tracking-wide">I'm not a robot</label>
                   </div>
                   <img src="https://www.gstatic.com/recaptcha/api2/logo_48.png" alt="Captcha" className="h-8 opacity-50" />
                </div>

                <button 
                  onClick={handleGenerate}
                  disabled={loading || !captchaChecked}
                  className={`w-full py-6 text-sm font-bold tracking-[0.3em] uppercase transition-all duration-500 ${loading || !captchaChecked ? 'bg-neutral-800 cursor-not-allowed opacity-50' : 'bg-white text-black hover:bg-neutral-200'}`}
                >
                  {loading ? 'Processing Infrastructure...' : 'Unlock Scaling Strategy'}
                </button>
              </div>
            </div>
          ) : (
            <div className="animate-in fade-in slide-in-from-bottom-10 duration-1000 py-16 text-center">
              <div className="mb-8 inline-flex items-center justify-center w-20 h-20 rounded-full border border-white/20">
                <span className="text-3xl text-white">✓</span>
              </div>
              <div className="p-8 bg-white text-black font-bold tracking-tight">
                <p className="text-xl leading-relaxed">Thank you for your submission someone from our team will contact you shortly.</p>
              </div>
              <button 
                onClick={() => setSubmitted(false)}
                className="mt-12 text-[10px] uppercase tracking-[0.4em] font-bold opacity-30 hover:opacity-100 transition-opacity"
              >
                Back to Lab
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default StrategyLab;
